// Kanał podglądu ekranu (docs/admin/PROJEKT.md 5.2; worker-go/brama/podglad_ws.go): WebSocket `/brama/podglad/ws?sesja=`
// między klientem (nagrywa ekran APP - rrweb) a oglądającym (serwisant albo admin - widzi i klika). Brama tylko
// przekazuje ramki; sama dokłada „jest” / „wyszedl” (druga strona dołączyła / odeszła) i „koniec” (sesja skończona).
// Ramki: klient → {t:"e", l:[zdarzenia rrweb]}; oglądający → {t:"klik"}, {t:"wpis"}, {t:"przewin"} (id - węzeł z lustra
// rrweb, to samo po obu stronach). Ekran nie jest nigdzie zapisywany. Adres i żeton (aplikacja ze sklepu) - api/adres.ts.
import type { record as Nagrywanie } from "@rrweb/record";
import { gniazdo } from "../api/adres";
import { natywna } from "../uklad/instalacja";
import "../styles/ekran.css";

export type RamkaEkranu =
  | { t: "jest" } | { t: "wyszedl" } | { t: "koniec" }
  | { t: "e"; l: unknown[] }
  | { t: "klik"; id: number; x: number; y: number }
  | { t: "wpis"; id: number; v: string }
  | { t: "przewin"; id: number; dy: number };

export interface KanalEkranu {
  wyslij(r: RamkaEkranu): void;
  zamknij(): void;
}

const ODSTEPY_MS = [1000, 2000, 5000, 15000];
const PACZKA_MS = 40; // zdarzenia rrweb paczkami - mniej ramek przy przewijaniu i animacjach

/** Kanał sesji; po zerwaniu (telefon w tle, zmiana sieci) łączy się od nowa aż do „koniec” albo zamknij(). */
export function kanalEkranu(sesja: number, naRamke: (r: RamkaEkranu) => void, naPolaczenie?: (jest: boolean) => void): KanalEkranu {
  let ws: WebSocket | null = null;
  let koniec = false;
  let zegar = 0;
  let proba = 0;
  const polacz = async () => {
    const s = await gniazdo(`/brama/podglad/ws?sesja=${sesja}`);
    if (koniec) { s.close(); return; } // zamknięte w trakcie czytania żetonu
    ws = s;
    s.onopen = () => { proba = 0; naPolaczenie?.(true); };
    s.onmessage = (m) => {
      if (typeof m.data !== "string") return;
      let r: RamkaEkranu;
      try { r = JSON.parse(m.data) as RamkaEkranu; } catch { return; }
      if (r.t === "koniec") koniec = true;
      naRamke(r);
    };
    s.onclose = () => {
      if (ws !== s) return;
      ws = null;
      naPolaczenie?.(false);
      if (!koniec) zegar = window.setTimeout(() => void polacz(), ODSTEPY_MS[Math.min(proba++, ODSTEPY_MS.length - 1)]);
    };
  };
  void polacz();
  return {
    wyslij: (r) => { if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(r)); },
    zamknij: () => {
      koniec = true;
      window.clearTimeout(zegar);
      const s = ws;
      ws = null;
      s?.close();
    },
  };
}

// --- strona klienta: nagrywanie i polecenia oglądającego ---

/** Kropka w miejscu kliknięcia oglądającego - klient widzi, gdzie serwis klika. */
function wskaz(x: number, y: number): void {
  const d = document.createElement("div");
  d.className = "iot-ekran-klik";
  d.style.left = x + "px";
  d.style.top = y + "px";
  d.addEventListener("animationend", () => d.remove());
  document.body.append(d);
}

const POLE = "input:not([type=password]), textarea, select";

function klik(el: Element, x: number, y: number): void {
  const opcje = { bubbles: true, cancelable: true, view: window, clientX: x, clientY: y };
  el.dispatchEvent(new PointerEvent("pointerdown", opcje));
  el.dispatchEvent(new MouseEvent("mousedown", opcje));
  el.dispatchEvent(new PointerEvent("pointerup", opcje));
  el.dispatchEvent(new MouseEvent("mouseup", opcje));
  if (el.matches(POLE)) (el as HTMLElement).focus();
  if (el instanceof HTMLElement) el.click();
  else el.dispatchEvent(new MouseEvent("click", opcje));
}

/** Wartość pola przez natywny setter - React widzi zmianę jak przy pisaniu. Hasła nigdy. */
function wpisz(el: Element, v: string): void {
  if (!el.matches(POLE)) return;
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype
    : el instanceof HTMLSelectElement ? HTMLSelectElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, "value")?.set?.call(el, v);
  el.dispatchEvent(new Event("input", { bubbles: true }));
  el.dispatchEvent(new Event("change", { bubbles: true }));
}

function przewin(el: Element | null, dy: number): void {
  for (let n = el; n && n !== document.body; n = n.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(n).overflowY) && n.scrollHeight > n.clientHeight) {
      n.scrollBy({ top: dy });
      return;
    }
  }
  window.scrollBy({ top: dy });
}

/** Klient udostępnia ekran sesji: nagrywanie APP (hasła zamaskowane, elementy `iot-ekran-ukryj` zasłonięte), pełny obraz
 *  po każdym połączeniu i dołączeniu oglądającego, wykonywanie jego kliknięć i wpisów. Zwraca zatrzymanie. */
export function udostepnijEkran(sesja: number): () => void {
  let nagrywanie: typeof Nagrywanie | null = null;
  let stop: (() => void) | undefined;
  let zatrzymane = false;
  let paczka: unknown[] = [];
  let zegar = 0;
  const wezel = (id: number, x: number, y: number) => {
    const n = nagrywanie?.mirror.getNode(id);
    return n instanceof Element ? n : document.elementFromPoint(x, y);
  };
  const kanal = kanalEkranu(sesja, (r) => {
    switch (r.t) {
      case "jest":
        nagrywanie?.takeFullSnapshot(true);
        break;
      case "klik": {
        wskaz(r.x, r.y);
        const el = wezel(r.id, r.x, r.y);
        if (el) klik(el, r.x, r.y);
        break;
      }
      case "wpis": {
        const n = nagrywanie?.mirror.getNode(r.id);
        if (n instanceof Element) wpisz(n, r.v);
        break;
      }
      case "przewin": {
        const n = nagrywanie?.mirror.getNode(r.id);
        przewin(n instanceof Element ? n : null, r.dy);
        break;
      }
    }
  }, (jest) => { if (jest) nagrywanie?.takeFullSnapshot(true); });
  const wyslij = () => {
    zegar = 0;
    if (paczka.length === 0) return;
    kanal.wyslij({ t: "e", l: paczka });
    paczka = [];
  };
  void import("@rrweb/record").then(({ record }) => {
    if (zatrzymane) return;
    nagrywanie = record;
    stop = record({
      emit: (e) => { paczka.push(e); zegar ||= window.setTimeout(wyslij, PACZKA_MS); },
      maskInputOptions: { password: true },
      blockClass: "iot-ekran-ukryj",
      sampling: { mousemove: 50, scroll: 100, input: "last" },
      inlineStylesheet: true,
      // Aplikacja ze sklepu: obrazki to adresy blob tego telefonu - oglądający ich nie otworzy, idą w nagraniu.
      inlineImages: natywna(),
      recordCanvas: false,
      collectFonts: false,
    });
  });
  return () => {
    zatrzymane = true;
    stop?.();
    window.clearTimeout(zegar);
    kanal.zamknij();
  };
}
